/* import __COLOCATED_TEMPLATE__ from './side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type ApplicationInstance from '@ember/application/instance';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';
import type LanguagesService from 'embercom/services/languages-service';
import type GlossaryService from 'embercom/services/glossary-service';
import type NotificationsService from 'embercom/services/notifications-service';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { post, put } from 'embercom/lib/ajax';
import { modifier } from 'ember-modifier';
import { set } from '@ember/object';
import { normalizeLocale } from 'embercom/components/inbox2/conversation-stream/helpers/language-selection';

interface Translation {
  [locale: string]: string;
}

interface GlossaryItem {
  id?: number;
  created_at?: string;
  updated_at?: string;
  translations: Translation;
  non_translatable: boolean;
}

interface SideDrawerArgs {
  isOpen: boolean;
  onClose: (rowData: GlossaryItem | undefined) => void;
  mode: 'new' | 'edit';
  showCloseIcon?: boolean;
  id?: string;
  rowData?: GlossaryItem;
}

export default class SideDrawer extends Component<SideDrawerArgs> {
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare session: Session;
  @service declare appService: AppService;
  @service declare fetch: $TSFixMe;
  @service declare languagesService: LanguagesService;
  @service declare glossaryService: GlossaryService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @tracked nonTranslatable = false;
  @tracked translations: Translation = {};
  @tracked rowData?: GlossaryItem;
  @tracked mode: 'new' | 'edit' = 'new';

  // Keep track of whether we've initialized with the current rowData - using plain property, not tracked
  private _initializedRowDataId?: number;

  // Modifier to watch for changes in args.rowData and reinitialize if needed
  watchRowData = modifier(() => {
    // Check for ID change OR mode change
    let currentRowDataId = this.args.rowData?.id;
    let currentMode = this.args.mode;

    // Initialize if either ID changed OR drawer was reopened in 'new' mode
    if (
      this._initializedRowDataId !== currentRowDataId ||
      (currentMode === 'new' && this.args.isOpen)
    ) {
      this._initializedRowDataId = currentRowDataId;
      this.initializeData();
    }

    return () => {};
  });

  constructor(owner: ApplicationInstance, args: SideDrawerArgs) {
    super(owner, args);
    this.initializeData();
  }

  @action
  initializeData() {
    if (this.args.mode === 'new') {
      // Always reset data in new mode
      this.nonTranslatable = false;
      this.translations = { [this.appLocale]: '' };
      this.mode = 'new';
    } else if (this.args.rowData) {
      this.nonTranslatable = this.args.rowData.non_translatable || false;

      // Handle properly structured glossary item
      if (this.args.rowData.translations) {
        this.translations = { ...this.args.rowData.translations };
      }
      // Handle table row format (which has direct locale keys)
      else {
        let translations: Translation = {};

        // Get all keys that might be locale codes
        let localeKeys = Object.keys(this.args.rowData).filter((key) =>
          // Exclude known non-translation fields, but don't exclude 'id' if it's a string value (Indonesian locale)
          key === 'id'
            ? typeof (this.args.rowData as any)[key] === 'string'
            : !['non_translatable', 'created_at', 'updated_at', 'isHovered'].includes(key),
        );

        localeKeys.forEach((locale) => {
          let value = (this.args.rowData as any)[locale];
          if (typeof value === 'string') {
            translations[locale] = value;
          }
        });

        this.translations = translations;
      }

      this.mode = 'edit';
    }
  }

  @action
  deleteGlossary(rowData: GlossaryItem) {
    this.glossaryService.deleteGlossary(rowData.id);
    this.args.onClose(undefined);
  }

  get isNewSidebar() {
    return this.args.mode === 'new';
  }

  get headerTitle() {
    return this.isNewSidebar
      ? this.intl.t('new-settings.workspace.multilingual.new-glossary')
      : this.intl.t('new-settings.workspace.multilingual.edit-glossary');
  }

  get appLocale(): string {
    // Prefer the workspace's default locale from languages-service; fallback to teammate UI locale
    let defaultFromWorkspace = this.languagesService.languages?.defaultLocale;
    return normalizeLocale(defaultFromWorkspace || this.session.locale)!;
  }

  get localeDropdownItems() {
    if (!this.languagesService.languages?.supportedLocales) {
      return [];
    }

    // Create the default app locale item
    let appLocaleItem = {
      value: this.appLocale,
      // Include human-readable language name for clarity, e.g. "Default Phrase: English (en)"
      text: `${this.intl.t('new-settings.workspace.multilingual.default-phrase')}: ${this.getLanguageDisplayName(this.appLocale)} (${this.appLocale})`,
      isDefault: true,
    };

    // Filter and map the other permitted locales
    let otherLocales = this.languagesService.languages.supportedLocales
      .filter((locale: any) => locale.isPermitted && locale.localeId !== this.appLocale)
      .map((locale: any) => {
        return {
          value: locale.localeId,
          text: `${locale.name} (${locale.localeId})`,
          isDefault: false,
        };
      });

    // Return the app locale item first, followed by other locales
    return [appLocaleItem, ...otherLocales];
  }

  get displayLocales() {
    if (this.args.mode === 'edit') {
      // Get all possible locales from all sources
      let allLocales = new Set<string>([
        // 1. App locale
        this.appLocale,
        // 2. All locales in our local translations object
        ...Object.keys(this.translations || {}),
        // 3. All supported locales from settings
        ...(this.languagesService?.languages?.supportedLocales
          ?.filter((locale: any) => locale.isPermitted)
          .map((locale: any) => locale.localeId) || []),
      ]);

      return this.localeDropdownItems.filter((locale) => allLocales.has(locale.value));
    } else {
      if (this.nonTranslatable) {
        return this.localeDropdownItems.filter((locale) => locale.isDefault);
      }
      return this.localeDropdownItems;
    }
  }

  getLanguageFromCode(languageCode: string, locale: string): string {
    try {
      let baseLanguageCode = languageCode.split('-')[0];
      let languageNames = new Intl.DisplayNames([locale], { type: 'language' });
      return languageNames.of(languageCode) || languageNames.of(baseLanguageCode) || languageCode;
    } catch (e) {
      return languageCode;
    }
  }

  getLanguageDisplayName(langCode: string): string {
    return this.getLanguageFromCode(langCode, this.session.locale);
  }

  @action
  updateTranslation(locale: string, event: Event) {
    let target = event.target as HTMLInputElement;
    let value = target.value;

    // Instead of recreating the entire translations object, use Ember's set to
    // update individual properties, which preserves focus better
    set(this.translations, locale, value);
  }

  @action
  async handleSave() {
    // Validate the default locale input isn't empty
    let defaultLocaleValue = this.translations[this.appLocale];
    if (!defaultLocaleValue || defaultLocaleValue.trim() === '') {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.multilingual.glossary.notification.default-required'),
      );
      return;
    }

    if (this.args.mode === 'edit' && this.args.rowData) {
      // For edit mode, build the return object by combining the original data
      // with our updated fields
      this.rowData = {
        // Keep the original id and metadata
        id: this.args.rowData.id,
        created_at: this.args.rowData.created_at,
        updated_at: this.args.rowData.updated_at,
        // Use our tracked state for the updated fields
        translations: { ...this.translations },
        non_translatable: this.nonTranslatable,
      };
    } else {
      // New mode
      this.rowData = {
        translations: { ...this.translations },
        non_translatable: this.nonTranslatable,
      };
    }

    // Save first and wait for it to complete
    await taskFor(this.saveGlossaryItem).perform();

    // Then close with the updated data - the parent component will handle reloading
    this.handleClose(this.rowData);
  }

  @dropTask
  *saveGlossaryItem(): TaskGenerator<void> {
    try {
      // When in edit mode, directly use the original rowData to ensure we have all fields
      let glossaryId = this.args.rowData?.id;

      // Create params with our tracked translations object which contains ALL translations
      // Because we initialized it with ALL translations in initializeData() and only update
      // the specific locales that are changed in updateTranslation()
      let params = {
        app_id: this.session.workspace.id,
        translations: this.translations, // Contains all translations
        non_translatable: this.nonTranslatable,
      };

      if (this.isNewSidebar) {
        yield post(`/ember/multilingual_glossary`, params);
      } else if (glossaryId) {
        yield put(`/ember/multilingual_glossary/${glossaryId}`, params);
      }

      this.intercomEventService.trackAnalyticsEvent({
        action: this.isNewSidebar ? 'created' : 'updated',
        object: 'glossary_term',
        section: 'workspace_settings',
        place: 'multilingual',
      });

      this.notificationsService.notifyConfirmation(
        this.isNewSidebar
          ? this.intl.t('new-settings.workspace.multilingual.glossary.notification.add-success')
          : this.intl.t('new-settings.workspace.multilingual.glossary.notification.edit-success'),
      );
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.multilingual.glossary.notification.error'),
      );
      this.args.onClose(undefined);
    }
  }

  @action
  toggleNonTranslatable() {
    this.nonTranslatable = !this.nonTranslatable;

    // If non-translatable is true, only keep the app locale
    if (this.nonTranslatable) {
      let appLocaleValue = this.translations[this.appLocale] || '';
      this.translations = { [this.appLocale]: appLocaleValue };
    }
  }

  @action
  handleClose(rowData: GlossaryItem | undefined) {
    // Only pass data back if we're saving (rowData is defined)
    // Otherwise, just close without changes
    this.args.onClose(rowData);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Multilingual::SideDrawer': typeof SideDrawer;
  }
}
